import { useFormContext } from "react-hook-form";

import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { POCKET_COLOR_PALETTE, POCKET_ICONS } from "../constants/pockets.constants";
import type { WalletIcon } from "../types/pockets-api.types";
import { PocketIcon } from "./PocketIcon";

export const PocketFormFields = () => {
  const { t } = useTranslations("cards");
  const { watch, setValue } = useFormContext();

  const name = (watch("name") as string) ?? "";
  const color = (watch("color") as string) ?? POCKET_COLOR_PALETTE[0];
  const icon = (watch("icon") as WalletIcon) ?? "default";

  return (
    <div className="space-y-5">
      <ColocaInputField
        name="name"
        label={t("pocket_label_name")}
        placeholder={t("pocket_label_name_placeholder")}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{t("pocket_label_color")}</label>
        <div className="flex flex-wrap gap-2">
          {POCKET_COLOR_PALETTE.map((hex) => {
            const active = hex === color;
            return (
              <button
                key={hex}
                type="button"
                onClick={() =>
                  setValue("color", hex, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                aria-label={hex}
                aria-pressed={active}
                className={cn(
                  "relative h-8 w-8 rounded-md transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active && "scale-110 ring-2 ring-ring ring-offset-2 ring-offset-background",
                )}
                style={{ background: hex }}
              />
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{t("pocket_label_icon")}</label>
        <div className="grid grid-cols-6 gap-2 sm:grid-cols-11">
          {POCKET_ICONS.map((descriptor) => {
            const Icon = descriptor.icon;
            const active = descriptor.id === icon;
            return (
              <button
                key={descriptor.id}
                type="button"
                onClick={() =>
                  setValue("icon", descriptor.id, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                title={t(descriptor.labelKey as never)}
                aria-label={t(descriptor.labelKey as never)}
                aria-pressed={active}
                className={cn(
                  "inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-border/80",
                )}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <PocketIcon icon={icon} color={color} size={36} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">
            {name.trim() || t("pocket_label_name_placeholder")}
          </div>
          <div className="text-[11px] text-muted-foreground">{t("pocket_preview_label")}</div>
        </div>
      </div>
    </div>
  );
};
